import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc, updateDoc, deleteDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { FaHeart, FaUserPlus, FaBan, FaFlag, FaForward, FaTrash, FaChevronUp, FaChevronDown, FaPaperPlane } from 'react-icons/fa';
import { getAnonymousDisplayName } from '../utils/emojiUtils';
import { containsProfanity, containsLink } from '../utils/profanityFilter';
import YapperModal from '../components/YapperModal';
import { IconButton } from '../components/ds/core/IconButton';
import { Input } from '../components/ds/core/Input';
import { Avatar } from '../components/ds/core/Avatar';
import { CountryTag } from '../components/globe/CountryTag';
import './ConversationView.css';

const regionName = (iso) => {
  if (!iso) return '';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso.toUpperCase()) || iso;
  } catch {
    return iso;
  }
};

function ConversationView({ conversationId, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [areFriends, setAreFriends] = useState(false);
  const [loading, setLoading] = useState(true);
  const [themeColors, setThemeColors] = useState({ bg: '#667eea', secondary: '#764ba2' });
  const [isAdminBroadcast, setIsAdminBroadcast] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null, showCancel: false });
  const userId = localStorage.getItem('userId');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const showAlert = (title, message, type = 'info') => {
    setModal({ isOpen: true, title, message, type, onConfirm: () => setModal(m => ({ ...m, isOpen: false })), showCancel: false });
  };

  const showConfirm = (title, message, onConfirmCallback, type = 'warning') => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setModal(m => ({ ...m, isOpen: false }));
        if (onConfirmCallback) onConfirmCallback();
      },
      showCancel: true
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollUp = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollBy({ top: -300, behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollBy({ top: 300, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    loadConversation();
    loadUserTheme();
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
      const scrollAmount = e.deltaY > 0 ? 300 : -300;
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollBy({
          top: scrollAmount,
          behavior: 'smooth'
        });
      }
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [loading, messages]);


  const loadUserTheme = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.themeBackgroundColor) {
          setThemeColors({
            bg: userData.themeBackgroundColor,
            secondary: userData.themeEarthColor || userData.themeBackgroundColor
          });
        }
      }
    } catch (err) {
      console.error('Failed to load user theme:', err);
    }
  };

  const loadConversation = async () => {
    try {
      // Get conversation
      const conversationDoc = await getDoc(doc(db, 'conversations', conversationId));
      const conversationData = conversationDoc.data();

      // Check if this is an admin broadcast conversation
      if (conversationData.isAdminBroadcast) {
        setIsAdminBroadcast(true);
      }

      // Get other user ID
      const otherUserId = conversationData.participants.find(id => id !== userId);

      // Get other user data
      const otherUserDoc = await getDoc(doc(db, 'users', otherUserId));
      const rawData = otherUserDoc.data();
      const otherUserData = {
        id: otherUserDoc.id,
        username: rawData?.username || null,
        countryCode: rawData?.countryCode || null,
        likes: rawData?.likes || 0,
      };

      // Check if friends
      const friendshipQuery = query(
        collection(db, 'friendships'),
        where('participants', 'array-contains', userId)
      );
      const friendshipSnapshot = await getDocs(friendshipQuery);
      const isFriend = friendshipSnapshot.docs.some(doc =>
        doc.data().participants.includes(otherUserId) && doc.data().status === 'accepted'
      );

      setOtherUser(otherUserData);
      setAreFriends(isFriend);

      // Load messages
      const messagesQuery = query(
        collection(db, 'messages'),
        where('conversationId', '==', conversationId)
      );
      const messagesSnapshot = await getDocs(messagesQuery);
      const loadedMessages = messagesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0));

      // Mark unread messages as read
      for (const message of messagesSnapshot.docs) {
        const messageData = message.data();
        if (messageData.receiverId === userId && messageData.read === false) {
          await updateDoc(doc(db, 'messages', message.id), { read: true });
        }
      }

      setMessages(loadedMessages);
      setLoading(false);
    } catch (error) {
      console.error('Error loading conversation:', error);
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    // Check for profanity
    if (containsProfanity(newMessage)) {
      showAlert('Content Warning', 'Your message contains inappropriate language and cannot be sent. Please keep Yapp a friendly place.', 'warning');
      return;
    }

    // Check for links
    if (containsLink(newMessage)) {
      showAlert('Links Not Allowed', 'Links are not allowed in messages. Please remove any URLs from your message.', 'warning');
      return;
    }

    setSending(true);
    try {
      // Get sender's country code for message stats
      const senderDoc = await getDoc(doc(db, 'users', userId));
      const senderCountryCode = senderDoc.data()?.countryCode || null;

      // Get receiver's country code
      const receiverCountryCode = otherUser.countryCode || null;

      const messageData = {
        conversationId,
        senderId: userId,
        receiverId: otherUser.id,
        text: newMessage.trim(),
        timestamp: serverTimestamp(),
        read: false
      };

      // Only add senderCountryCode if it exists
      if (senderCountryCode) {
        messageData.senderCountryCode = senderCountryCode;
      }

      await addDoc(collection(db, 'messages'), messageData);

      // Update conversation last message
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: newMessage.trim(),
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Anonymous message stats are tallied server-side by the countMessageByCountry
      // Cloud Function (the stats doc is not client-writable).

      setNewMessage('');
      loadConversation();
    } catch (error) {
      console.error('Error sending message:', error);
      showAlert('Error', 'Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleAddFriend = async () => {
    // Check if user is a guest
    if (localStorage.getItem('isGuest') === 'true') {
      showAlert('Guest Restriction', 'Guest accounts cannot add friends. Create an account to unlock this feature!', 'warning');
      return;
    }

    try {
      await addDoc(collection(db, 'friendships'), {
        participants: [userId, otherUser.id],
        requesterId: userId,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      showAlert('Friend Request Sent', 'Friend request sent!', 'success');
    } catch (error) {
      console.error('Error sending friend request:', error);
      showAlert('Error', 'Failed to send friend request', 'error');
    }
  };

  const handleLike = async () => {
    try {
      // Check if user already liked this person
      const existingLikeQuery = query(
        collection(db, 'likes'),
        where('fromUserId', '==', userId),
        where('toUserId', '==', otherUser.id)
      );
      const existingLikes = await getDocs(existingLikeQuery);

      if (!existingLikes.empty) {
        showAlert('Already Liked', 'You already liked this user!', 'info');
        return;
      }

      // Increment the user's like count
      await updateDoc(doc(db, 'users', otherUser.id), {
        likes: increment(1)
      });

      // Track who liked
      await addDoc(collection(db, 'likes'), {
        fromUserId: userId,
        toUserId: otherUser.id,
        timestamp: serverTimestamp()
      });

      showAlert('Like Sent', 'Like sent! ❤️', 'success');
    } catch (error) {
      console.error('Error sending like:', error);
      showAlert('Error', 'Failed to send like: ' + error.message, 'error');
    }
  };

  const handleBlock = async () => {
    showConfirm('Block User', `Are you sure you want to block ${areFriends ? otherUser.username : 'this user'}?`, async () => {
      try {
        await addDoc(collection(db, 'blocks'), {
          blockerId: userId,
          blockedId: otherUser.id,
          timestamp: serverTimestamp()
        });

        // Delete the conversation
        await deleteDoc(doc(db, 'conversations', conversationId));

        // Delete all messages in the conversation
        const messagesQuery = query(
          collection(db, 'messages'),
          where('conversationId', '==', conversationId)
        );
        const messagesSnapshot = await getDocs(messagesQuery);
        for (const messageDoc of messagesSnapshot.docs) {
          await deleteDoc(doc(db, 'messages', messageDoc.id));
        }

        // Remove friendship if they were friends
        if (areFriends) {
          const friendshipQuery = query(
            collection(db, 'friendships'),
            where('participants', 'array-contains', userId)
          );
          const friendshipSnapshot = await getDocs(friendshipQuery);
          for (const friendDoc of friendshipSnapshot.docs) {
            const friendData = friendDoc.data();
            if (friendData.participants.includes(otherUser.id)) {
              await deleteDoc(doc(db, 'friendships', friendDoc.id));
            }
          }
        }

        showAlert('User Blocked', 'User blocked successfully', 'success');
        onBack();
      } catch (error) {
        console.error('Error blocking user:', error);
        showAlert('Error', 'Failed to block user', 'error');
      }
    });
  };

  const handleReport = async () => {
    // Check if user has already reported this person
    try {
      const existingReportQuery = query(
        collection(db, 'reports'),
        where('reporterId', '==', userId),
        where('reportedId', '==', otherUser.id)
      );
      const existingReports = await getDocs(existingReportQuery);

      if (!existingReports.empty) {
        showAlert('Already Reported', 'You have already reported this user. Our moderation team is currently reviewing your previous report and will take appropriate action. Thank you for helping keep our community safe.', 'info');
        return;
      }
    } catch (error) {
      console.error('Error checking existing reports:', error);
      showAlert('Error', 'Unable to process your report at this time. Please try again later.', 'error');
      return;
    }

    // Open a new popup window
    const width = 500;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const reportWindow = window.open(
      '',
      'ReportUser',
      `width=${width},height=${height},left=${left},top=${top},resizable=no,scrollbars=yes`
    );

    if (reportWindow) {
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Report User</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
              padding: 2rem;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              min-height: 100vh;
            }
            .container {
              background: white;
              padding: 2rem;
              border-radius: 16px;
              box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
              max-width: 450px;
              margin: 0 auto;
            }
            h1 {
              color: #333;
              font-size: 1.75rem;
              margin-bottom: 0.5rem;
              display: flex;
              align-items: center;
              gap: 0.5rem;
            }
            .subtitle {
              color: #666;
              font-size: 0.95rem;
              margin-bottom: 1.5rem;
              line-height: 1.5;
            }
            .report-options {
              display: flex;
              flex-direction: column;
              gap: 0.75rem;
              margin-bottom: 1.5rem;
            }
            .report-option {
              display: flex;
              align-items: center;
              gap: 0.75rem;
              padding: 1rem;
              border: 2px solid #e2e8f0;
              border-radius: 10px;
              cursor: pointer;
              transition: all 0.2s;
              background: white;
            }
            .report-option:hover {
              border-color: #667eea;
              background: rgba(102, 126, 234, 0.05);
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
            }
            .report-option input[type="radio"] {
              width: 20px;
              height: 20px;
              cursor: pointer;
              accent-color: #667eea;
            }
            .report-option label {
              font-size: 1rem;
              color: #333;
              font-weight: 500;
              cursor: pointer;
              flex: 1;
            }
            .other-input {
              margin-top: 1rem;
              padding: 1rem;
              background: #f7fafc;
              border-radius: 10px;
              display: none;
            }
            .other-input.show {
              display: block;
            }
            textarea {
              width: 100%;
              padding: 0.75rem;
              border: 2px solid #e2e8f0;
              border-radius: 8px;
              font-size: 0.95rem;
              font-family: inherit;
              resize: vertical;
              min-height: 100px;
              outline: none;
              transition: all 0.2s;
            }
            textarea:focus {
              border-color: #667eea;
              box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
            }
            .word-count {
              display: block;
              margin-top: 0.5rem;
              color: #666;
              font-size: 0.85rem;
            }
            .buttons {
              display: flex;
              gap: 1rem;
              margin-top: 1.5rem;
            }
            button {
              flex: 1;
              padding: 0.875rem 1.5rem;
              border: none;
              border-radius: 8px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
            }
            .btn-cancel {
              background: #e2e8f0;
              color: #333;
            }
            .btn-cancel:hover {
              background: #cbd5e0;
            }
            .btn-submit {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
            }
            .btn-submit:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
            }
            .btn-submit:disabled {
              opacity: 0.5;
              cursor: not-allowed;
              transform: none;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>🚫 Report User</h1>
            <p class="subtitle">Please select a reason for reporting this user. Your report helps keep Yapp safe for everyone.</p>

            <div class="report-options">
              <div class="report-option">
                <input type="radio" name="reason" value="hate-speech" id="hate-speech">
                <label for="hate-speech">Hate Speech</label>
              </div>

              <div class="report-option">
                <input type="radio" name="reason" value="harassment" id="harassment">
                <label for="harassment">Harassment</label>
              </div>

              <div class="report-option">
                <input type="radio" name="reason" value="spam" id="spam">
                <label for="spam">Spam</label>
              </div>

              <div class="report-option">
                <input type="radio" name="reason" value="inappropriate-content" id="inappropriate">
                <label for="inappropriate">Inappropriate Content</label>
              </div>

              <div class="report-option">
                <input type="radio" name="reason" value="other" id="other">
                <label for="other">Other</label>
              </div>
            </div>

            <div class="other-input" id="otherInput">
              <textarea id="otherText" placeholder="Please describe the reason (max 200 words)"></textarea>
              <span class="word-count" id="wordCount">0 / 200 words</span>
            </div>

            <div class="buttons">
              <button class="btn-cancel" onclick="window.close()">Cancel</button>
              <button class="btn-submit" id="submitBtn">Submit Report</button>
            </div>
          </div>

          <script>
            const otherRadio = document.getElementById('other');
            const otherInput = document.getElementById('otherInput');
            const otherText = document.getElementById('otherText');
            const wordCount = document.getElementById('wordCount');
            const submitBtn = document.getElementById('submitBtn');
            const radios = document.querySelectorAll('input[name="reason"]');

            // Show/hide other input
            radios.forEach(radio => {
              radio.addEventListener('change', () => {
                if (radio.value === 'other') {
                  otherInput.classList.add('show');
                } else {
                  otherInput.classList.remove('show');
                }
              });
            });

            // Word counter
            otherText.addEventListener('input', (e) => {
              const words = e.target.value.trim().split(/\\s+/).filter(w => w.length > 0);
              const count = words.length;
              wordCount.textContent = count + ' / 200 words';

              if (count > 200) {
                const limited = words.slice(0, 200).join(' ');
                e.target.value = limited;
                wordCount.textContent = '200 / 200 words';
              }
            });

            // Submit report
            submitBtn.addEventListener('click', async () => {
              const selectedReason = document.querySelector('input[name="reason"]:checked');

              if (!selectedReason) {
                alert('Please select a reason for reporting');
                return;
              }

              if (selectedReason.value === 'other' && !otherText.value.trim()) {
                alert('Please describe the reason for reporting');
                return;
              }

              const reason = selectedReason.value === 'other' ? otherText.value.trim() : selectedReason.value;

              submitBtn.disabled = true;
              submitBtn.textContent = 'Submitting...';

              try {
                // Send report data to parent window
                window.opener.postMessage({
                  type: 'SUBMIT_REPORT',
                  data: {
                    reporterId: ${JSON.stringify(userId)},
                    reportedId: ${JSON.stringify(otherUser.id)},
                    reason: reason
                  }
                }, window.location.origin);

                alert('User reported. Thank you for keeping Yapp safe.');
                window.close();
              } catch (error) {
                alert('Failed to submit report. Please try again.');
                submitBtn.disabled = false;
                submitBtn.textContent = 'Submit Report';
              }
            });
          </script>
        </body>
        </html>
      `);
      reportWindow.document.close();
    }
  };

  // Listen for report submission from popup
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.type === 'SUBMIT_REPORT') {
        try {
          // Double-check if user has already reported this person (race condition prevention)
          const existingReportQuery = query(
            collection(db, 'reports'),
            where('reporterId', '==', event.data.data.reporterId),
            where('reportedId', '==', event.data.data.reportedId)
          );
          const existingReports = await getDocs(existingReportQuery);

          if (!existingReports.empty) {
            return;
          }

          // Get chat history between the two users
          const chatHistory = messages.map(msg => ({
            senderId: msg.senderId,
            text: msg.text,
            timestamp: msg.timestamp?.toDate?.()?.toISOString() || new Date().toISOString(),
            senderIsReporter: msg.senderId === userId
          }));

          await addDoc(collection(db, 'reports'), {
            reporterId: event.data.data.reporterId,
            reportedId: event.data.data.reportedId,
            reason: event.data.data.reason,
            chatHistory: chatHistory,
            conversationId: conversationId,
            timestamp: serverTimestamp(),
            status: 'pending'
          });

        } catch (error) {
          console.error('Error submitting report:', error);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [messages, userId, conversationId]);

  const handleSkip = async () => {
    showConfirm('Skip Conversation', 'Skip this conversation? This will end the conversation for both parties.', async () => {
      try {
        // Delete the conversation for both parties
        await deleteDoc(doc(db, 'conversations', conversationId));

        // Optionally delete all messages
        const messagesQuery = query(
          collection(db, 'messages'),
          where('conversationId', '==', conversationId)
        );
        const messagesSnapshot = await getDocs(messagesQuery);
        for (const messageDoc of messagesSnapshot.docs) {
          await deleteDoc(doc(db, 'messages', messageDoc.id));
        }

        showAlert('Conversation Ended', 'Conversation ended', 'success');
        onBack();
      } catch (error) {
        console.error('Error skipping conversation:', error);
        showAlert('Error', 'Failed to skip conversation', 'error');
      }
    });
  };

  const handleDeleteConversation = async () => {
    showConfirm('Delete Conversation', 'Are you sure you want to delete this conversation? This action cannot be undone.', async () => {
      try {
        // Delete all messages in the conversation
        const messagesQuery = query(
          collection(db, 'messages'),
          where('conversationId', '==', conversationId)
        );
        const messagesSnapshot = await getDocs(messagesQuery);

        for (const messageDoc of messagesSnapshot.docs) {
          await deleteDoc(doc(db, 'messages', messageDoc.id));
        }

        // Delete the conversation
        await deleteDoc(doc(db, 'conversations', conversationId));

        showAlert('Conversation Deleted', 'Conversation deleted successfully', 'success');
        onBack();
      } catch (error) {
        console.error('Error deleting conversation:', error);
        showAlert('Error', 'Failed to delete conversation', 'error');
      }
    });
  };

  if (loading) {
    return <div className="conversation-loading">Loading conversation...</div>;
  }

  return (
    <div className="conversation-view">
      <div className="conversation-header">
        <IconButton
          variant="glass"
          label="Back"
          onClick={onBack}
          className="back-icon-btn"
          type="button"
        >
          <span aria-hidden="true">←</span>
        </IconButton>
        <Avatar
          name={areFriends ? otherUser.username : getAnonymousDisplayName(otherUser.id)}
          kind={areFriends ? 'friend' : 'anonymous'}
          size={44}
        />
        <div className="header-identity">
          <div className="user-name-section">
            <h2>
              {areFriends ? otherUser.username : getAnonymousDisplayName(otherUser.id)}
            </h2>
            {areFriends && (
              <span className="friend-badge">Friend</span>
            )}
          </div>
          {otherUser.countryCode && (
            <CountryTag
              iso={otherUser.countryCode}
              name={regionName(otherUser.countryCode)}
              tone="soft"
              size="sm"
              pin
            />
          )}
        </div>
        <div className="conversation-actions">
          {areFriends && otherUser?.likes !== undefined && (
            <div className="user-likes-display">
              <span className="likes-heart">❤️</span>
              <span className="likes-count">{otherUser.likes || 0}</span>
            </div>
          )}
          <button
            onClick={handleLike}
            className="action-btn like-btn"
            title="Like"
            type="button"
          >
            <FaHeart />
          </button>
          {!areFriends && (
            <button
              onClick={handleSkip}
              className="action-btn skip-btn"
              title="Skip"
              type="button"
            >
              <FaForward />
            </button>
          )}
          {!areFriends && (
            <button
              onClick={handleAddFriend}
              className="action-btn friend-btn"
              title="Add Friend"
              type="button"
            >
              <FaUserPlus />
            </button>
          )}
          <button
            onClick={handleDeleteConversation}
            className="action-btn delete-btn"
            title="Delete Conversation"
            type="button"
          >
            <FaTrash />
          </button>
          <button
            onClick={handleBlock}
            className="action-btn block-btn"
            title="Block"
            type="button"
          >
            <FaBan />
          </button>
          <button
            onClick={handleReport}
            className="action-btn report-btn"
            title="Report"
            type="button"
          >
            <FaFlag />
          </button>
        </div>
      </div>

      <div className="messages-container" ref={messagesContainerRef}>
        <div className="thread-intro-note">
          {areFriends
            ? `You and ${otherUser.username} are friends${otherUser.countryCode ? ` — ${regionName(otherUser.countryCode)}` : ''}`
            : `You were matched with a stranger${otherUser.countryCode ? ` from ${regionName(otherUser.countryCode)}` : ''}`}
        </div>
        {messages.map(msg => (
          <div
            key={msg.id}
            className={`message ${msg.senderId === userId ? 'sent' : 'received'} ${msg.isAdminBroadcast ? 'admin-broadcast' : ''}`}
          >
            <div
              className="message-content"
              style={msg.senderId !== userId && msg.isAdminBroadcast ? {
                background: `linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 165, 0, 0.1))`,
                border: '2px solid rgba(255, 215, 0, 0.4)'
              } : {}}
            >
              {msg.isAdminBroadcast && (
                <div className="admin-broadcast-header">
                  <span className="admin-star">⭐</span>
                  <span className="admin-label">Admin {msg.adminUsername || ''}</span>
                  <span className="broadcast-label">• Announcement</span>
                </div>
              )}
              <p>{msg.text}</p>
              <span className="message-time">
                {msg.timestamp?.toDate().toLocaleTimeString()}
              </span>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="scroll-controls">
        <button
          className="scroll-btn scroll-up-btn"
          onClick={scrollUp}
          title="Scroll up"
          style={{ backgroundColor: themeColors.bg }}
        >
          <FaChevronUp />
        </button>
        <button
          className="scroll-btn scroll-down-btn"
          onClick={scrollDown}
          title="Scroll down"
          style={{ backgroundColor: themeColors.bg }}
        >
          <FaChevronDown />
        </button>
      </div>

      {isAdminBroadcast && (
        <div className="admin-broadcast-notice">
          <span className="admin-star">⭐</span>
          <span>This is an admin announcement. You cannot reply to this message.</span>
        </div>
      )}

      <form
        onSubmit={handleSendMessage}
        className="message-input-form"
      >
        <Input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={isAdminBroadcast ? "Cannot reply to admin announcements" : "Type a message..."}
          disabled={sending || isAdminBroadcast}
          maxLength={1000}
          className="composer-input"
        />
        <IconButton
          variant="brand"
          size="lg"
          label="Send"
          type="submit"
          disabled={sending || !newMessage.trim()}
        >
          <FaPaperPlane />
        </IconButton>
      </form>

      <YapperModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        onCancel={modal.showCancel ? () => setModal(m => ({ ...m, isOpen: false })) : null}
      />
    </div>
  );
}

export default ConversationView;
